import React, { useEffect, useState } from 'react';
import TopNav from '../components/TopNav.jsx';
import { SpinnerOverlay } from '../components/index.jsx';
import { getCreditHistory } from '../api/auth';

const pageTheme = {
  pageBg: '#F8FAFC',
  pageBgSoft: '#EEF2FF',
  surface: '#FFFFFF',
  surfaceSoft: '#F8FAFC',
  border: '#E5E7EB',
  text: '#111827',
  body: '#374151',
  muted: '#9CA3AF',
  accent: '#4F46E5',
};

const formatDate = (value) => {
  if (!value) return 'Recently';
  try {
    return new Date(value).toLocaleString([], {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return 'Recently';
  }
};

export default function CreditHistory() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [count, setCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrevious, setHasPrevious] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const data = await getCreditHistory(page, pageSize);
        if (!cancelled) {
          setItems(data?.results || []);
          setCount(data?.count || 0);
          setTotalPages(data?.total_pages || 1);
          setHasNext(Boolean(data?.has_next));
          setHasPrevious(Boolean(data?.has_previous));
        }
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.error || 'Could not load credit history right now.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [page, pageSize]);

  return (
    <div style={{ minHeight: '100vh', background: `linear-gradient(180deg, ${pageTheme.pageBgSoft} 0%, ${pageTheme.pageBg} 100%)` }}>
      <TopNav backTo="/platform" backLabel="المنصة" title="سجل الرصيد" containerWidth="1120px" />
      <div style={{ maxWidth: 1120, margin: '0 auto', padding: '28px 20px 40px', display: 'grid', gap: 18 }}>
        <div style={{ background: pageTheme.surface, border: `1px solid ${pageTheme.border}`, borderRadius: 24, padding: 24, boxShadow: '0 18px 44px rgba(15,23,42,.07)' }}>
          <div style={{ fontSize: 12, letterSpacing: '.14em', textTransform: 'uppercase', color: pageTheme.muted, fontWeight: 800, marginBottom: 10 }}>
            رصيد الذكاء الاصطناعي
          </div>
          <div style={{ fontSize: 28, fontWeight: 900, color: pageTheme.text, marginBottom: 6 }}>أحدث حركات الرصيد</div>
          <div style={{ color: pageTheme.body, lineHeight: 1.7 }}>
            يظهر هنا كل رصيد تم خصمه أو إضافته أو استرجاعه حتى يعرف الطالب بالضبط ما الذي تم احتسابه ولماذا.
          </div>
          <div style={{ marginTop: 12, fontSize: 13, color: pageTheme.muted, fontWeight: 700 }}>
            إجمالي السجلات: {count}
          </div>
        </div>

        {loading ? <SpinnerOverlay text="Loading credit history…" /> : null}
        {!loading && error ? (
          <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', color: '#991B1B', borderRadius: 18, padding: 18 }}>
            {error}
          </div>
        ) : null}
        {!loading && !error && items.length === 0 ? (
          <div style={{ background: pageTheme.surface, border: `1px dashed ${pageTheme.border}`, color: pageTheme.body, borderRadius: 18, padding: 22 }}>
            لا توجد حركات رصيد حتى الآن.
          </div>
        ) : null}

        {!loading && !error && items.length > 0 ? (
          <div style={{ display: 'grid', gap: 16 }}>
            <div style={{ display: 'grid', gap: 12 }}>
              {items.map((item) => {
                const isCredit = item.delta >= 0;
                return (
                  <div key={item.id} style={{ background: pageTheme.surface, border: `1px solid ${pageTheme.border}`, borderRadius: 20, padding: 18, boxShadow: '0 14px 32px rgba(15,23,42,.05)' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
                      <div style={{ display: 'grid', gap: 8 }}>
                        <div style={{ fontSize: 16, fontWeight: 800, color: pageTheme.text }}>{item.description}</div>
                        <div style={{ fontSize: 13, color: pageTheme.body }}>{formatDate(item.created_at)}</div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 20, fontWeight: 900, color: isCredit ? '#047857' : '#B91C1C' }}>
                          {isCredit ? '+' : ''}{item.delta}
                        </div>
                        <div style={{ fontSize: 12, color: pageTheme.muted, fontWeight: 700 }}>
                          الرصيد بعد العملية: {item.balance_after}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', background: pageTheme.surface, border: `1px solid ${pageTheme.border}`, borderRadius: 18, padding: 16 }}>
              <div style={{ fontSize: 13, color: pageTheme.body, fontWeight: 700 }}>
                الصفحة {page} من {totalPages}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <button
                  type="button"
                  onClick={() => setPage((current) => Math.max(1, current - 1))}
                  disabled={!hasPrevious || loading}
                  style={{ height: 40, borderRadius: 999, border: `1px solid ${pageTheme.border}`, background: hasPrevious ? pageTheme.surfaceSoft : '#F3F4F6', color: hasPrevious ? pageTheme.text : pageTheme.muted, padding: '0 16px', fontWeight: 800, cursor: hasPrevious ? 'pointer' : 'not-allowed' }}
                >
                  السابق
                </button>
                <button
                  type="button"
                  onClick={() => setPage((current) => current + 1)}
                  disabled={!hasNext || loading}
                  style={{ height: 40, borderRadius: 999, border: `1px solid ${pageTheme.accent}`, background: hasNext ? pageTheme.accent : '#C7D2FE', color: '#fff', padding: '0 16px', fontWeight: 800, cursor: hasNext ? 'pointer' : 'not-allowed' }}
                >
                  التالي
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
